// 動画 URL → iframe 埋込用 URL への変換 (SSOT)。
// server (admin.ts の保存時変換) / admin 画面プレビュー / RichTextEditor から
// 同じ関数を使い、変換結果がずれないようにする。
//
// 重要: Vimeo の「限定公開 (unlisted)」動画は URL に プライバシーハッシュ を含む。
//   https://vimeo.com/{ID}/{HASH}
// この HASH を落とすと player 側が非公開扱いになり
// 「大変申し訳ありません / エラーが発生しました」が表示される。
// 埋込には https://player.vimeo.com/video/{ID}?h={HASH} が必須。

// Vimeo のプライバシーハッシュ (実績値は 16 進 10 桁)。
// 16 進に限定すると、非 16 進の文字が混ざったハッシュを
// 「途中まで」拾って壊れた URL を作ってしまうため英数字を許容する。
// 併せて末尾に境界を要求し、ハッシュの途中で切れないようにする。
const VIMEO_HASH = "[0-9a-zA-Z]{6,64}";
// ハッシュの直後は 区切り文字 or 終端 でなければならない
const HASH_END = "(?![0-9a-zA-Z])";

// https://vimeo.com/1213166525/0c41057f03?share=copy
const VIMEO_WITH_HASH = new RegExp(
  `(?:^|//)(?:www\\.)?vimeo\\.com/(\\d+)/(${VIMEO_HASH})${HASH_END}`,
);
// https://player.vimeo.com/video/1213166525/0c41057f03 (パス形式の埋込 URL)
const VIMEO_PLAYER_PATH_HASH = new RegExp(
  `player\\.vimeo\\.com/video/(\\d+)/(${VIMEO_HASH})${HASH_END}`,
);
// https://player.vimeo.com/video/1213166525?h=0c41057f03 (正規の埋込 URL)
const VIMEO_PLAYER_QUERY_HASH = new RegExp(
  `player\\.vimeo\\.com/video/(\\d+)(?:\\?|&).*?\\bh=(${VIMEO_HASH})${HASH_END}`,
);
// https://vimeo.com/1213166525?h=0c41057f03
const VIMEO_QUERY_HASH = new RegExp(
  `(?:^|//)(?:www\\.)?vimeo\\.com/(\\d+)(?:\\?|&).*?\\bh=(${VIMEO_HASH})${HASH_END}`,
);
// ハッシュ無し (公開動画)
const VIMEO_PLAIN = /(?:^|\/\/)(?:www\.)?vimeo\.com\/(\d+)/;
const VIMEO_PLAYER_PLAIN = /player\.vimeo\.com\/video\/(\d+)/;

const YOUTUBE_WATCH = /youtube\.com\/watch\?v=([A-Za-z0-9_-]+)/;
const YOUTUBE_SHORT = /youtu\.be\/([A-Za-z0-9_-]+)/;
const YOUTUBE_SHORTS = /youtube\.com\/shorts\/([A-Za-z0-9_-]+)/;
const YOUTUBE_EMBED = /youtube\.com\/embed\/([A-Za-z0-9_-]+)/;

function vimeoEmbed(id: string, hash?: string | null): string {
  return hash
    ? `https://player.vimeo.com/video/${id}?h=${hash}`
    : `https://player.vimeo.com/video/${id}`;
}

// Vimeo / YouTube の「埋め込みコード」貼り付け対応。
// Vimeo の共有ダイアログは URL ではなく下記のような HTML を配る。
//   <div style="padding:56.25% 0 0 0;..."><iframe src="https://player.vimeo.com/video/{ID}?h={HASH}&amp;badge=0&amp;..." ...></iframe></div>
//   <script src="https://player.vimeo.com/api/player.js"></script>
// ここから iframe の src だけを取り出す。wrapper div と script は捨てる
// (レスポンシブ化は会員側 / 管理側の aspect-video ラッパが担当するため不要)。
const IFRAME_SRC = /<iframe[^>]*?\ssrc\s*=\s*["']([^"']+)["']/i;

// 埋め込みコードから取り出した src は、素性が確認できるホストのみ許可する。
// 任意 HTML 由来の URL をそのまま iframe に流さないための歯止め。
const EMBED_SRC_ALLOWED =
  /^https:\/\/(?:player\.vimeo\.com\/video\/|(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/|(?:www\.)?vimeo\.com\/|(?:www\.)?youtube\.com\/watch|youtu\.be\/)/i;

// HTML 属性値なので &amp; 等が実体参照になっている。最低限を復元する。
function decodeHtmlEntities(s: string): string {
  return s
    .replace(/&(?:amp|#0*38|#[xX]0*26);/g, "&")
    .replace(/&(?:quot|#0*34);/g, '"')
    .replace(/&(?:apos|#0*39);/g, "'")
    .replace(/&(?:lt|#0*60);/g, "<")
    .replace(/&(?:gt|#0*62);/g, ">");
}

/**
 * 入力が埋め込みコード (HTML) なら iframe の src を取り出して返す。
 * URL 直貼りなど iframe が無い場合は null。
 * 許可ホスト外の src は null (= 埋め込みコードとしては受け付けない)。
 */
function extractIframeSrc(input: string): string | null {
  const m = input.match(IFRAME_SRC);
  if (!m) return null;
  const src = decodeHtmlEntities(m[1]).trim();
  if (!EMBED_SRC_ALLOWED.test(src)) return null;
  // 実体参照を戻した結果 引用符や山括弧が現れる入力は受け付けない
  // (この値は iframe の src 属性へ埋め込まれるため)
  if (/["'<>\s]/.test(src)) return null;
  return src;
}

/**
 * Vimeo / YouTube の共有 URL を iframe の src に使える埋込 URL へ変換する。
 *
 * - 限定公開 Vimeo のプライバシーハッシュは `?h=` として保持する
 * - `?share=copy` などの余計なクエリは落として正規化する
 * - 認識できない形式は https:// の場合のみ そのまま返す
 *   (javascript: / data: 等が iframe src に流れ込むのを防ぐ)
 * - 空文字・未認識かつ非 https は null
 */
export function toVideoEmbedUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed === "") return null;

  // 埋め込みコード (HTML) を貼られた場合は iframe の src を取り出して続行する。
  // iframe があるのに src が取り出せない / 許可ホスト外なら、
  // 本文を URL として誤解釈せず ここで弾く。
  let url = trimmed;
  if (/<iframe/i.test(trimmed)) {
    const src = extractIframeSrc(trimmed);
    if (!src) return null;
    url = src;
  }

  // --- Vimeo (ハッシュ有りを先に判定すること) ---
  // 既に ?h= 付きの正規な埋込 URL は加工しない。
  // Vimeo の埋込コードに含まれる badge / autopause / player_id 等の
  // 表示オプションを落とさないため。
  if (VIMEO_PLAYER_QUERY_HASH.test(url) && /^https:\/\//i.test(url)) return url;

  // パス形式 player.vimeo.com/video/{id}/{hash} は ?h= 形式へ正規化する。
  // 残りのクエリがあれば引き継ぐ。
  const playerPathHash = url.match(VIMEO_PLAYER_PATH_HASH);
  if (playerPathHash) {
    const rest = url.split("?")[1];
    const base = vimeoEmbed(playerPathHash[1], playerPathHash[2]);
    return rest ? `${base}&${rest}` : base;
  }

  const queryHash = url.match(VIMEO_QUERY_HASH);
  if (queryHash) return vimeoEmbed(queryHash[1], queryHash[2]);

  const withHash = url.match(VIMEO_WITH_HASH);
  if (withHash) return vimeoEmbed(withHash[1], withHash[2]);

  // ハッシュ無しの player URL も、既に埋込形式なので加工せず通す。
  const playerPlain = url.match(VIMEO_PLAYER_PLAIN);
  if (playerPlain) {
    return /^https:\/\//i.test(url) ? url : vimeoEmbed(playerPlain[1]);
  }

  const plain = url.match(VIMEO_PLAIN);
  if (plain) return vimeoEmbed(plain[1]);

  // --- YouTube ---
  // 既に embed 形式なら加工しない。
  // 埋め込みコード由来の start / si 等のパラメータを保持するため
  // (Vimeo の player URL と同じ扱い)。
  if (
    /^https:\/\/(?:www\.)?youtube(?:-nocookie)?\.com\/embed\/[A-Za-z0-9_-]+/i.test(
      url,
    )
  ) {
    return url;
  }
  const yt =
    url.match(YOUTUBE_WATCH) ??
    url.match(YOUTUBE_SHORT) ??
    url.match(YOUTUBE_SHORTS) ??
    url.match(YOUTUBE_EMBED);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;

  // --- 未認識 ---
  // 既存挙動 (そのまま保存) を維持しつつ、iframe src に危険なスキームが
  // 入らないよう https:// のみ通す。
  if (/^https:\/\//i.test(url)) return url;
  return null;
}

/**
 * Vimeo / YouTube として確実に認識できた URL かどうか。
 * RichTextEditor の「動画」ボタンは認識できた場合のみ埋め込む。
 */
export function isKnownVideoUrl(raw: string | null | undefined): boolean {
  if (!raw) return false;
  const url = raw.trim();
  return (
    VIMEO_PLAIN.test(url) ||
    VIMEO_PLAYER_PLAIN.test(url) ||
    YOUTUBE_WATCH.test(url) ||
    YOUTUBE_SHORT.test(url) ||
    YOUTUBE_SHORTS.test(url) ||
    YOUTUBE_EMBED.test(url)
  );
}
